import { useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import noop from 'utils/noop';
import { FormField } from 'ui/formField';
import { InputText } from 'ui/inputs';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'ui/modal';
import { PrimaryButton } from 'ui/button';
import { BasicSelect } from 'ui/selects';
import { createSendingModalDropdownOptions } from './utils';
import { createSendingDomainRequest } from '../actions';
import { getIsLoading, getEspAccounts } from '../selectors';
import { useValidatedDomain } from '../hooks';

// type ModalProps = {
//   onClose: () => {},
// };

export const CreateSendingDomainModal = ({ onClose = noop }) => {
  const dispatch = useDispatch();
  const [newDomain, setNewDomain, errorMessage] = useValidatedDomain('');
  const isLoading = useSelector(getIsLoading);
  const espAccounts = useSelector(getEspAccounts);
  const [espAccount, setEspAccount] = useState(espAccounts[0]);
  const dropdownOptions = useMemo(
    () => createSendingModalDropdownOptions(espAccounts),
    [espAccounts],
  );

  const useEspAccountsDropdown = dropdownOptions.length > 0;

  const handleDropdownChange = useCallback(
    ([value]) => {
      // the dropdown uses the espAccounts ids as values
      const account = espAccounts.find(({ id }) => id === value);
      setEspAccount(account);
    },
    [espAccounts],
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    const espId = useEspAccountsDropdown ? espAccount.id : null;
    dispatch(createSendingDomainRequest(newDomain, espId));
    onClose();
  };

  const hasValidationError = errorMessage.length > 0;
  const isDisabled = hasValidationError || isLoading || newDomain.length === 0;

  return (
    <Modal
      id="createSendingDomainModal"
      isModalOpen
      data-test="mailDomains-create-sending-domain-modal"
      onClose={onClose}
    >
      <ModalHeader>Create a new sending domain</ModalHeader>
      <ModalBody>
        <form id="createNewSendingDomain" onSubmit={handleSubmit}>
          <FormField
            hasError={hasValidationError}
            key="newSendingDomain"
            errorText={errorMessage}
            inputId="mailDomains-create-sending-domain-modal-form"
            labelText="Sending domain"
            isRequired
          >
            <InputText
              autoFocus
              id="newSendingDomain"
              error={hasValidationError}
              value={newDomain}
              onChange={(value) => setNewDomain(value)}
              data-test="mailDomains-create-sending-domain-modal-form-input"
              placeholder="mycompany.com"
              required
            />
          </FormField>
          {useEspAccountsDropdown && (
            <FormField
              key="sendingDomainEsp"
              inputId="espAccountSelector"
              labelText="Select the ESP"
            >
              <BasicSelect
                isFullWidth
                options={dropdownOptions}
                defaultValues={[espAccount.id]}
                onSelect={handleDropdownChange}
              />
            </FormField>
          )}
        </form>
      </ModalBody>
      <ModalFooter hasCancel onClose={onClose}>
        <PrimaryButton
          isDisabled={isDisabled}
          key="submit"
          type="submit"
          form="createNewSendingDomain"
          data-test="mailDomains-create-sending-domain-modal-confirm-button"
        >
          Create
        </PrimaryButton>
      </ModalFooter>
    </Modal>
  );
};
